import { useState } from 'react';
import { Link } from '../../components/Link';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type { AddonTier, Plan, PlanKind, WarmupTierPublic } from '../../lib/types';
import { useAddonTiers } from '../../lib/useAddonTiers';
import { useBillingMode } from '../../lib/useBillingMode';
import { useFeatures } from '../../lib/useFeatures';
import { Icon } from '../../components/icons';
import { CreditCostTable } from '../../components/CreditCostTable';
import { PublicFooter } from '../../components/PublicFooter';
import { PublicHeader } from '../../components/PublicHeader';
import { PlansExplainer } from '../../components/PlansExplainer';
import { InfoHint } from '../../components/InfoHint';
import { Button, Spinner, cn } from '../../components/ui';
import { breadcrumbSchema, Seo, SOFTWARE_ID } from '../../lib/seo';
import { money } from '../../lib/format';

/**
 * Public pricing page. Two product lines (Basic vs Verify) are presented as tabs so the
 * price comparison within a line isn't distorted by the other tier's pricing. Below the
 * grid, the shared PlansExplainer renders the feature comparison matrix, the
 * verification-credits-vs-anomaly-credits anatomy, and the FAQ — same component used in
 * the in-app Subscription page so the marketing and the in-app upgrade stories stay
 * in lockstep.
 */
export function PricingPage() {
  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['plans', 'public'],
    queryFn: () => api<Plan[]>('/plans', { auth: false }),
  });
  // Wallet vs legacy switch — drives hero copy, the plan card subtitle, the
  // feature list, and the "How credits work" panel below the grid. Comes from
  // /platform/billing-mode which the super-admin controls via /admin/settings/credits.
  const billingMode = useBillingMode();
  // Per-plan Product+Offer JSON-LD so Google can show price ranges in rich SERP cards
  // for "sieveguard pricing" queries. Built from the same /plans payload that drives
  // the visible grid, so the structured data and the rendered prices can never drift.
  // Empty until plans load — useSeo re-emits when the array changes.
  const pricingJsonLd = plans
    .filter((p) => p.active && p.monthlyAmountCents > 0)
    .map((p) => ({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `SieveGuard ${p.name}`,
      description: p.description ?? `${p.name} plan on SieveGuard.`,
      brand: { '@type': 'Brand', name: 'SieveGuard' },
      offers: {
        '@type': 'Offer',
        url: 'https://sieveguard.com/pricing/',
        priceCurrency: p.currency.toUpperCase(),
        price: (p.monthlyAmountCents / 100).toFixed(2),
        availability: 'https://schema.org/InStock',
        priceSpecification: {
          '@type': 'UnitPriceSpecification',
          price: (p.monthlyAmountCents / 100).toFixed(2),
          priceCurrency: p.currency.toUpperCase(),
          unitCode: 'MON',
        },
      },
    }));
  // Attach the REAL price range to the same SoftwareApplication entity the homepage declares (by
  // @id), built from the same live plans as the visible grid so it can't drift. This is what
  // replaces the old homepage `price: 0` — the product entity now carries an AggregateOffer
  // spanning the cheapest→dearest active plan, so an LLM reads accurate pricing, not "free".
  const paidPlans = plans.filter((p) => p.active && p.monthlyAmountCents > 0);
  const softwareOfferJsonLd = paidPlans.length
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'SoftwareApplication',
          '@id': SOFTWARE_ID,
          name: 'SieveGuard',
          applicationCategory: 'SecurityApplication',
          operatingSystem: 'Web',
          offers: {
            '@type': 'AggregateOffer',
            priceCurrency: paidPlans[0]!.currency.toUpperCase(),
            lowPrice: (Math.min(...paidPlans.map((p) => p.monthlyAmountCents)) / 100).toFixed(2),
            highPrice: (Math.max(...paidPlans.map((p) => p.monthlyAmountCents)) / 100).toFixed(2),
            offerCount: paidPlans.length,
            url: 'https://sieveguard.com/pricing/',
          },
        },
      ]
    : [];
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [kind, setKind] = useState<PlanKind>('basic');
  const visible = plans.filter((p) => p.kind === kind);
  const anyYearly = plans.some((p) => p.yearlyAmountCents != null);

  return (
    <>
      <Seo
        title="Pricing"
        description={billingMode.unifiedWalletEnabled
          ? 'Transparent monthly and yearly plans. One credit wallet per plan — Basic covers Bot Detection + Verification; Pro adds blacklist, inbox placement and more.'
          : 'Transparent monthly and yearly plans. Anomaly detection on every tier, mailbox verification on Pro. 2 months free on your first yearly subscription.'}
        path="/pricing"
        jsonLd={[
          breadcrumbSchema([
            { name: 'Home', url: 'https://sieveguard.com/' },
            { name: 'Pricing', url: 'https://sieveguard.com/pricing' },
          ]),
          ...softwareOfferJsonLd,
          ...pricingJsonLd,
        ]}
      />
    <div className="mx-auto max-w-[1210px] overflow-x-clip px-6 pb-20">
      <PublicHeader />

      <section className="mx-auto max-w-2xl py-14 text-center">
        <h1 className="font-display text-4xl font-extrabold tracking-[-0.02em] md:text-5xl">
          Simple, credit-based pricing
        </h1>
        <p className="mx-auto mt-4 max-w-lg text-[15px] leading-relaxed text-muted">
          {billingMode.unifiedWalletEnabled
            ? 'One wallet, every feature. Each plan grants a monthly pool of SieveGuard Credits that you spend on detection, blacklist checks, inbox-placement tests, verification, and phone validation — at the per-action cost shown below.'
            : 'Every plan includes bot/anomaly detection, clustering, and the full API. Verify plans add authoritative mailbox verification on top.'}
        </p>

        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          {/* Product-line tabs — switches between the Basic and Pro tiers. Wallet
              mode labels them by what the wallet UNLOCKS (Basic: the coupled
              main action; Pro: the full feature suite). Legacy labels retained
              behind the flag so an operator running the old model gets the old
              copy without a re-deploy. */}
          <div className="inline-flex items-center gap-1 rounded-full border border-border bg-panel p-1">
            {(['basic', 'verify'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setKind(k)}
                className={cn(
                  'rounded-full px-4 py-1.5 text-[13px] font-medium transition',
                  kind === k ? 'bg-accent text-black' : 'text-muted hover:text-txt',
                )}
              >
                {billingMode.unifiedWalletEnabled
                  ? k === 'verify'
                    ? 'Pro · Full suite'
                    : 'Basic · Bot Detection + Verification'
                  : k === 'verify'
                    ? 'Pro · Verify + Anomaly'
                    : 'Basic · Anomaly only'}
              </button>
            ))}
          </div>

          {anyYearly && (
            <div className="inline-flex items-center gap-1.5 rounded-full border border-border bg-panel p-1">
              {(['month', 'year'] as const).map((iv) => (
                <button
                  key={iv}
                  onClick={() => setInterval(iv)}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-[13px] font-medium transition',
                    interval === iv ? 'bg-accent text-black' : 'text-muted hover:text-txt',
                  )}
                >
                  {iv === 'month' ? 'Monthly' : 'Yearly'}
                  {iv === 'year' && (
                    <span className={cn('ml-1.5', interval === iv ? 'text-black/70' : 'text-allow')}>
                      2 mo free · 1st yr
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        <p className="mt-5 text-[12.5px] text-faint">
          {billingMode.unifiedWalletEnabled
            ? kind === 'verify'
              ? 'Every wallet-metered feature — Bot Detection + Verification (coupled), Blacklist, Inbox Placement, Phone Validation, Domain Health. All from one credit pool.'
              : 'Bot Detection + Verification only. Every /v1/emails call runs both layers as one coupled action against your credit wallet.'
            : kind === 'verify'
              ? 'Mailbox-level verification + anomaly detection. Includes a bonus of extra anomaly-only screenings.'
              : 'Pure anomaly / bot-cluster detection. No mailbox-level verification, no per-request credit consumption.'}
        </p>
        {interval === 'year' && (
          <p className="mt-2 text-[12.5px] text-allow">
            Yearly is 10% off vs paying monthly all year — and your <strong>first</strong> yearly
            invoice gets 2 months free as a welcome bonus. Renewals charge the regular 10%-off rate.
          </p>
        )}
        {/* Single global price + a fine-print line explaining how tax actually lands
            per customer profile. EU B2B with a valid VAT ID = reverse charge; non-EU
            buyers = no VAT (export of services); consumers / no-VAT-ID = local rate
            added by Stripe Tax at checkout. */}
        <p className="mt-3 text-[11.5px] text-faint">
          Prices shown excl. VAT.{' '}
          <span className="text-muted">
            EU businesses with a valid VAT ID pay no VAT (reverse charge). Non-EU customers
            pay no VAT. Consumers pay VAT at their country's rate — added at checkout.
          </span>
        </p>
      </section>

      {isLoading ? (
        <Spinner />
      ) : visible.length === 0 ? (
        <p className="text-center text-sm text-faint">
          No {kind === 'verify' ? 'Verify' : 'Basic'} plans available yet — check back soon.
        </p>
      ) : (
        <section className="flex flex-wrap justify-center gap-4">
          {visible.map((p) => (
            <div
              key={p.id}
              className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(25%-0.75rem)]"
            >
              <PlanCard plan={p} interval={interval} firstYearPromoAvailable />
            </div>
          ))}
        </section>
      )}

      {billingMode.unifiedWalletEnabled && <HowCreditsWorkPanel billingMode={billingMode} />}

      <AddonsSection />

      <PlansExplainer />

      <section className="mt-16 rounded-2xl border border-bordersoft bg-gradient-to-b from-panel to-panel2 p-10 text-center">
        <h2 className="font-display text-2xl font-bold tracking-tight">Need a custom volume?</h2>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted">
          Higher allowances and annual commitments are available — start free and reach out from the
          console.
        </p>
        <Link to="/register" className="mt-6 inline-block">
          <Button variant="primary" className="h-12 px-6">
            Create your workspace <Icon.Play width={15} height={15} />
          </Button>
        </Link>
      </section>

      <PublicFooter />
    </div>
    </>
  );
}

/**
 * Mirrors `FIRST_YEAR_PERCENT_OFF` in apps/api/src/billing/stripe.service.ts. Used to
 * preview the first-year discounted invoice on the plan card before the tenant lands
 * on Stripe Checkout — the value the tenant actually pays is computed by Stripe from
 * the coupon, not from this constant, so a small marketing-vs-billing rounding is fine.
 */
const FIRST_YEAR_PERCENT_OFF = 7.41;

/**
 * A single plan card. Shared between the public Pricing page and the in-app Subscription
 * page so they can't drift. Tooltips on the credit-related rows deep-link to the
 * `#how-credits-work` section in the explainer below.
 */
export function PlanCard({
  plan: p,
  interval,
  currentPlanId,
  onSubscribe,
  disabled,
  firstYearPromoAvailable = false,
}: {
  plan: Plan;
  interval: 'month' | 'year';
  currentPlanId?: string;
  onSubscribe?: () => void;
  disabled?: boolean;
  /** When true and `interval === 'year'`, the card previews the discounted first-year invoice. */
  firstYearPromoAvailable?: boolean;
}) {
  const { unifiedWalletEnabled } = useBillingMode();
  const yearly = interval === 'year' && p.yearlyAmountCents != null;
  const cents = yearly ? p.yearlyAmountCents! : p.monthlyAmountCents;
  const perMonth = yearly ? Math.round(p.yearlyAmountCents! / 12) : p.monthlyAmountCents;
  const isCurrent = currentPlanId === p.id;
  const anomalyBonus = (p.quotas.emailsPerMonth ?? 0) - p.verifierCredits;
  const firstYearCents =
    yearly && firstYearPromoAvailable
      ? Math.round(p.yearlyAmountCents! * (1 - FIRST_YEAR_PERCENT_OFF / 100))
      : null;
  const firstYearPerMonth = firstYearCents != null ? Math.round(firstYearCents / 12) : null;

  return (
    <div
      className={cn(
        'flex h-full flex-col rounded-2xl border bg-panel p-6',
        isCurrent ? 'border-accentdeep' : 'border-bordersoft',
      )}
    >
      <div className="text-base font-semibold text-txt">{p.name}</div>
      {/* Subtitle = the headline metric for the plan kind so it always matches the
          name. The wallet world reads "X SieveGuard Credits / mo" + a feature-access
          line ("full feature access" / "detection only"); the legacy world reads
          "X verifications / mo" or "X anomaly screenings / mo". The two-line wallet
          format is what gives prospects the most immediate cost intuition. */}
      {unifiedWalletEnabled ? (
        <>
          <div className="mt-1 font-mono text-[12px] text-accent">
            {p.monthlyCredits.toLocaleString()} SieveGuard Credits / mo
          </div>
          <div className="mt-0.5 text-[11.5px] text-faint">
            {p.kind === 'verify' ? 'Full feature access' : 'Bot Detection + Verification'}
          </div>
        </>
      ) : (
        <>
          {p.kind === 'verify' && p.verifierCredits > 0 && (
            <div className="mt-1 font-mono text-[12px] text-accent">
              {p.verifierCredits.toLocaleString()} verifications / mo
            </div>
          )}
          {p.kind === 'basic' && (p.quotas.emailsPerMonth ?? 0) > 0 && (
            <div className="mt-1 font-mono text-[12px] text-allow">
              {(p.quotas.emailsPerMonth ?? 0).toLocaleString()} anomaly screenings / mo
            </div>
          )}
        </>
      )}
      <div className="mt-4 font-display text-3xl font-bold">
        {money(cents, p.currency)}
        <span className="text-sm font-normal text-faint">/{yearly ? 'yr' : 'mo'}</span>
      </div>
      {yearly && (
        <div className="mt-1 text-[12px] text-muted">
          ≈ {money(perMonth, p.currency)}/mo billed annually
        </div>
      )}
      {firstYearCents != null && firstYearPerMonth != null && (
        <div className="mt-2 rounded-md border border-allow/30 bg-allow/5 px-2.5 py-1.5 text-[12px] text-allow">
          <div className="font-semibold">
            First year: {money(firstYearCents, p.currency)}
          </div>
          <div className="text-[11.5px] text-allow/80">
            ≈ {money(firstYearPerMonth, p.currency)}/mo · 2 months free, renews at{' '}
            {money(cents, p.currency)}/yr
          </div>
        </div>
      )}
      {/* Under the wallet regime, `p.description` (seeded as "X verifications + Y
          anomaly screenings") is the pre-wallet quota phrasing — misleading because
          the two counts imply independent quotas when the wallet has one pool that
          shares between them at different per-action weights. The wallet bullets
          below already spell out the credit pool + feature scope, so hide the
          legacy description string when the wallet is on. */}
      {p.description && !unifiedWalletEnabled && (
        <p className="mt-3 text-[13px] text-muted">{p.description}</p>
      )}

      <ul className="mt-4 flex flex-col gap-1.5 text-[13px] text-muted">
        {unifiedWalletEnabled ? (
          // Wallet mode: the headline is the credit pool + what it unlocks. Two ✓ rows
          // (credit allowance + feature scope), then Warmup bundle when applicable.
          <>
            <li className="flex items-start gap-2">
              <span className="text-allow">✓</span>
              <span className="flex-1">
                {p.monthlyCredits.toLocaleString()} SieveGuard Credits / month
                <InfoHint learnMoreTo="#how-credits-work">
                  One credit pool covers detection, blacklist, inbox placement, verification,
                  and phone validation. Mix-and-match against your monthly volume.
                </InfoHint>
              </span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-allow">✓</span>
              <span className="flex-1">
                {p.kind === 'verify'
                  ? 'Full feature surface: bot detection + verification (coupled) + blacklist + inbox placement + phone + domain health'
                  : 'Bot detection + email verification only — upgrade to Pro to unlock blacklist, inbox placement, phone, and domain health'}
              </span>
            </li>
            {p.includesWarmup && p.warmupInboxesPerMonth > 0 && (
              <li className="flex items-start gap-2">
                <span className="text-allow">✓</span>
                <span>
                  {p.warmupInboxesPerMonth.toLocaleString()} warm-up{' '}
                  {p.warmupInboxesPerMonth === 1 ? 'mailbox' : 'mailboxes'} included
                </span>
              </li>
            )}
          </>
        ) : (
          // Legacy mode: per-feature quotas (verification credits + anomaly screenings).
          <>
            {p.kind === 'verify' && p.verifierCredits > 0 && (
              <li className="flex items-start gap-2">
                <span className="text-allow">✓</span>
                <span className="flex-1">
                  {p.verifierCredits.toLocaleString()} mailbox verifications / mo
                  <InfoHint learnMoreTo="#how-credits-work">
                    Each mailbox-level check (deliverable / invalid / disposable / spamtrap) consumes
                    one verification credit per cache-miss address.
                  </InfoHint>
                </span>
              </li>
            )}
            {anomalyBonus > 0 && (
              <li className="flex items-start gap-2">
                <span className="text-allow">✓</span>
                <span className="flex-1">
                  {anomalyBonus.toLocaleString()}{' '}
                  {p.kind === 'verify' ? 'anomaly-only bonus / mo' : 'anomaly screenings / mo'}
                  <InfoHint learnMoreTo="#how-credits-work">
                    {p.kind === 'verify'
                      ? 'Extra headroom on top of your verification budget for months with an unusual signup spike.'
                      : 'Signup screenings drawn from this monthly budget — every /v1/emails call runs the full bot detection + mailbox verification stack.'}
                  </InfoHint>
                </span>
              </li>
            )}
            {p.kind === 'basic' && (
              <li className="flex items-start gap-2 text-faint">
                <span>—</span>
                <span>No mailbox verification</span>
              </li>
            )}
          </>
        )}
        <li className="flex items-start gap-2">
          <span className="text-allow">✓</span>
          <span>{p.quotas.apiKeys == null ? 'Unlimited' : p.quotas.apiKeys.toLocaleString()} API keys</span>
        </li>
        <li className="flex items-start gap-2">
          <span className="text-allow">✓</span>
          <span>{p.quotas.lists == null ? 'Unlimited' : p.quotas.lists.toLocaleString()} lists</span>
        </li>
      </ul>

      {/* mt-auto pushes the CTA to the card's bottom edge so the grid row's tallest
          card sets the floor and shorter cards let the empty space accumulate ABOVE
          the CTA — without this, a Pro 50k / 100k tile (extra warm-up bullet) leaves
          Pro 10k / 25k CTAs floating higher than their neighbours. `pt-6` keeps the
          minimum visual gap between the last bullet and the button so the tallest
          card doesn't touch. */}
      <div className="mt-auto pt-6">
        {onSubscribe ? (
          <Button
            variant={isCurrent ? 'ghost' : 'primary'}
            className="w-full"
            disabled={isCurrent || disabled}
            onClick={onSubscribe}
          >
            {isCurrent ? 'Current plan' : 'Subscribe'}
          </Button>
        ) : (
          <Link to="/register" className="block">
            <Button variant="primary" className="w-full">
              Get started
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Add-on slider tiers as a public-pricing comparison strip. Tiers are fetched live from
 * `/plans/addons` so a super-admin edit on /admin/addon-prices propagates here without a
 * redeploy. Mirrors the in-app slider on Profile → Add-ons but rendered as read-only
 * price cards. Domain Health has no slider (plan-bundled or super-admin-grant), so it's
 * surfaced as a separate "included with Pro" callout.
 */
/**
 * Each entry maps to a global `FeatureKind` via `addon`; the AddonsSection consumes
 * `useFeatures()` and skips any tile whose flag is OFF. Blurbs explicitly state what
 * a Trial or paid subscriber gets so prospects don't have to guess what's included.
 */
const ADDON_META: Array<{
  addon: 'blacklist' | 'inbox_placement' | 'phone_validation' | 'warmup_inbox';
  slug: string;
  title: string;
  blurb: string;
  unit: string;
  color: 'allow' | 'verify' | 'review';
}> = [
  {
    addon: 'inbox_placement' as const,
    slug: 'inbox-placement',
    title: 'Inbox Placement',
    blurb:
      'Diagnose where your mail actually lands. Send a tracking-coded test from your production sender to a seed list; we report inbox vs spam vs missing per ESP (Gmail / Yahoo / Outlook). 30 / 60 / 90-day sender-reputation trend rolls every completed test into one curve. Trial includes a few tests so you can see results before subscribing; Pro 25k+ bundles a monthly quota.',
    unit: 'tests',
    color: 'verify' as const,
  },
  {
    addon: 'warmup_inbox' as const,
    slug: 'warmup',
    title: 'Warmup',
    blurb:
      'Auto-warm your sending mailboxes (Gmail / Microsoft / SMTP). Pick a tier per mailbox — connect as many as you need and our partner network builds sender reputation via send/reply loops. Per-ESP reputation 0-100, per-mailbox health-check; higher tiers add your own templates, topic + language control and scheduling. The natural complement to Inbox Placement: diagnose where mail lands, then fix it. IP-level warmup is on our near-term roadmap.',
    unit: 'mailboxes',
    color: 'allow' as const,
  },
  {
    addon: 'phone_validation' as const,
    slug: 'phone-validation',
    title: 'Phone Validation',
    blurb:
      'Country / carrier / line-type lookup (mobile / VoIP / toll-free / fixed) on every number you submit. Cache-first — repeat lookups of the same number are free. Single + bulk modes. Bundled free on every Pro plan; the slider stacks extra quota on top of whatever your plan includes.',
    unit: 'lookups',
    color: 'allow' as const,
  },
  {
    addon: 'blacklist' as const,
    slug: 'blacklist-checks',
    title: 'Blacklist Checks',
    blurb:
      '50+ industry blacklists checked on every domain you ingest. Hit count + listing names surface on the matching Domain Health snapshot, so deliverability problems show up before they bite production traffic. Bundled free on Pro 10k+; the slider tops up.',
    unit: 'checks',
    color: 'review' as const,
  },
];

/**
 * "How credits work" — the wallet-mode explainer panel that goes between the plan
 * grid and the addons section. Three jobs:
 *  1. Show the per-action credit cost table (3 / 2 / 15 / 150 / 3) so prospects can
 *     do the mental arithmetic ("can I afford a Pro 25k plan if I run 2k IP tests?").
 *  2. Make the abstract "credit" concrete with a few mix-and-match examples on the
 *     reference plan tier.
 *  3. Mention credit packs as the overflow path — sets up the "you can top up if
 *     you grow past your plan allowance" narrative without distracting from plans.
 */
function HowCreditsWorkPanel({ billingMode }: { billingMode: ReturnType<typeof useBillingMode> }) {
  // Anchor the worked examples on a recognisable wallet size — 30,000 credits is the
  // entry Pro tier in the Phase-4 seed. Picked so the math reads cleanly without a
  // calculator. The actual examples adapt to the live cost table — if the operator
  // tunes the weights, these counts update automatically.
  const sample = 30_000;
  const c = billingMode.creditCosts;
  // Bot detection + email verification merged into one always-coupled action:
  // every /v1/emails call charges detection + verification (e.g. 3+2 = 5 cr) with
  // no verify-only or detection-only path. The example uses that combined weight
  // for the merged "screening" line so the number a prospect reads matches what
  // the API actually charges — no ambiguity about which flag runs which layer.
  const screeningCost = c.detection + c.emailVerification;
  const examples = [
    { label: 'signup screenings (bot detection + verification per call)', n: screeningCost > 0 ? Math.floor(sample / screeningCost) : 0 },
    { label: 'blacklist checks', n: c.blacklist > 0 ? Math.floor(sample / c.blacklist) : 0 },
    { label: 'inbox-placement tests', n: c.inboxPlacement > 0 ? Math.floor(sample / c.inboxPlacement) : 0 },
    { label: 'phone lookups', n: c.phoneValidation > 0 ? Math.floor(sample / c.phoneValidation) : 0 },
    { label: 'domain-health lookups', n: c.domainHealth > 0 ? Math.floor(sample / c.domainHealth) : 0 },
  ].filter((e) => e.n > 0);

  return (
    <section id="how-credits-work" className="mt-14 rounded-2xl border border-bordersoft bg-panel p-8">
      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight">How credits work</h2>
          <p className="mt-3 text-[14px] leading-relaxed text-muted">
            Every billable action draws from one credit pool. Bot Anomaly Detection and
            Email Verification run together on every signup screening at a combined
            per-call weight; blacklist checks, inbox placement, phone validation, and
            domain health each add their own weight when you use them. Whatever your
            monthly workload shape, the same wallet pays for all of it.
          </p>
          <p className="mt-3 text-[13.5px] text-muted">
            <strong>Example.</strong> {sample.toLocaleString()} credits buys you any single
            one of the following workloads (not additive — pick your mix):
          </p>
          <ul className="mt-2 flex flex-col gap-1 text-[13px] text-muted">
            {examples.map((e) => (
              <li key={e.label} className="flex items-baseline gap-2">
                <span className="text-allow">•</span>
                <span>
                  <span className="font-mono font-semibold text-txt">
                    {e.n.toLocaleString()}
                  </span>{' '}
                  {e.label}
                </span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-[12.5px] text-faint">
            Need more mid-month? One-shot credit packs top up your wallet without changing
            your subscription — see the Add-ons section below.
          </p>
        </div>

        <div className="rounded-xl border border-bordersoft bg-panel2 p-5">
          <div className="mb-3 text-[11px] font-mono uppercase tracking-[0.14em] text-faint">
            Per-action cost
          </div>
          <CreditCostTable
            costs={billingMode.creditCosts}
            note="Every action is billed in SieveGuard Credits — one wallet, one currency. Cache hits on repeat lookups are free within their TTL. The euro cost per credit depends on the plan or credit pack you buy — see the plan cards above for exact rates."
          />
        </div>
      </div>
    </section>
  );
}

function AddonsSection() {
  const { forAddon } = useAddonTiers('public');
  const { feature } = useFeatures();
  const { unifiedWalletEnabled, chargeUpgradesImmediately } = useBillingMode();
  // Under the wallet regime, only Warmup is still sold as a discrete add-on — its
  // per-mailbox billing model doesn't participate in the credit pool. Every other
  // former slider (Inbox Placement, Phone, Blacklist) and Domain Health are paid
  // from the SieveGuard Credits wallet, so their slider cards would only confuse
  // visitors reading the pricing page. The "How credits work" panel above already
  // shows the per-action credit weight.
  //
  // Under the legacy regime we keep the full slider grid + the "no per-check cost"
  // Domain Health callout — that's the world tenants signed up under before wallet
  // rollout.
  const visibleAddons = ADDON_META.filter((a) => feature(a.addon)).filter(
    (a) => !unifiedWalletEnabled || a.addon === 'warmup_inbox',
  );
  return (
    <section className="mt-20">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full border border-border bg-panel px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
          <span className="h-1.5 w-1.5 rounded-full bg-allow" /> Add-ons
        </span>
        <h2 className="mt-5 font-display text-3xl font-bold tracking-tight">
          {unifiedWalletEnabled ? 'One add-on that lives outside credits' : 'Stackable workspace add-ons'}
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-[14.5px] leading-relaxed text-muted">
          {unifiedWalletEnabled ? (
            <>
              With the credit wallet on, detection, verification, blacklist checks, inbox
              placement, phone lookups and domain health all draw from the same monthly
              credit pool — see{' '}
              <a href="#how-credits-work" className="text-accent hover:underline">
                How credits work
              </a>{' '}
              above. <strong className="text-txt">Warmup</strong> is the only feature still
              sold as a discrete add-on because it's billed per warming mailbox, not per
              action. Pick a tier when you connect each mailbox; it's Stripe-billed monthly
              at the tier's price, and{' '}
              {chargeUpgradesImmediately
                ? 'each addition is charged immediately (removals credit your next invoice).'
                : 'mid-cycle changes are pro-rated onto your next invoice.'}
            </>
          ) : (
            <>
              Bolt on extra signal layers from the{' '}
              <strong className="text-txt">Add-ons</strong> page in your workspace. Most add-ons
              use a monthly quota slider — pick the tier, Pro plans bundle some quota free, and
              the slider stacks on top with{' '}
              {chargeUpgradesImmediately
                ? 'immediate Stripe charges on upgrades (downgrades credit your next invoice)'
                : 'Stripe-pro-rated upgrades / downgrades onto your next invoice'}{' '}
              whenever you move it. <strong className="text-txt">Warmup</strong> is sold
              differently — per mailbox: pick a tier when you connect each warming inbox, billed
              monthly per mailbox at the tier's price ({chargeUpgradesImmediately
                ? 'additions charged immediately, removals credit next invoice'
                : 'pro-rated onto your next invoice'}). Every add-on is also available during
              Trial so you can validate the workflow before committing.
            </>
          )}
        </p>
      </div>

      <div
        className={`mt-10 grid gap-5 ${
          unifiedWalletEnabled ? 'mx-auto max-w-2xl' : 'lg:grid-cols-3'
        }`}
      >
        {visibleAddons.map((a) =>
          a.addon === 'warmup_inbox' ? (
            // Warmup is sold per-mailbox by tier (not a quota slider) — render its own
            // card sourced from the live tier catalog so the prices match the in-app
            // connect picker + the super-admin config.
            <WarmupAddonCard key={a.title} slug={a.slug} title={a.title} blurb={a.blurb} />
          ) : (
            <AddonCard
              key={a.title}
              slug={a.slug}
              title={a.title}
              blurb={a.blurb}
              unit={a.unit}
              color={a.color}
              tiers={forAddon(a.addon)}
            />
          ),
        )}
      </div>

      {/* Domain Health callout. Wallet mode reframes it as a credit-paid feature (still
          bundled inside Pro plans' monthly allowance, no separate slider) so the page
          doesn't leak the misleading "no per-check cost" phrasing while credits are on.
          Legacy mode keeps the original callout since DH really had zero per-call cost
          under that regime. */}
      {feature('domain_health') && (
        <div className="mt-5 rounded-2xl border border-bordersoft bg-panel p-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.14em] text-faint">
            {unifiedWalletEnabled
              ? 'Paid from your credit wallet · bundled inside every Pro plan'
              : 'Included with every Pro plan · also active during Trial'}
          </div>
          <h3 className="mt-1.5 text-lg font-semibold">Domain Health</h3>
          <p className="mt-1 text-[13.5px] leading-relaxed text-muted">
            {unifiedWalletEnabled ? (
              <>
                SPF / DKIM / DMARC / MX checker. Every domain you ingest gets a 0–100
                score that surfaces as a record badge — so weak senders get flagged
                before they cause damage. Each fresh lookup draws from your credit
                wallet at the weight shown in the cost table above; results are cached
                so repeat lookups within the TTL are free. Pro plans grant enough
                monthly credits that typical workloads never notice the weight.
              </>
            ) : (
              <>
                In-house SPF / DKIM / DMARC / MX checker. Every domain you ingest gets
                a 0–100 score that surfaces as a record badge — so weak senders get
                flagged before they cause damage. Trial workspaces get full access;
                Pro plans bundle it free permanently. No per-check cost.
              </>
            )}
          </p>
          <Link to="/features/domain-health" className="mt-3 inline-block font-mono text-[11px] text-accent hover:underline">
            Deep dive on Domain Health →
          </Link>
        </div>
      )}
    </section>
  );
}

function AddonCard({
  slug,
  title,
  blurb,
  tiers,
  unit,
  color,
}: {
  slug: string;
  title: string;
  blurb: string;
  tiers: ReadonlyArray<AddonTier>;
  unit: string;
  color: 'allow' | 'verify' | 'review';
}) {
  // Show the published tiers (skip the "Off" row — buyers don't need to see the no-op).
  const visible = tiers.filter((t) => t.quota > 0);
  const accent =
    color === 'allow' ? 'text-allow' : color === 'verify' ? 'text-verify' : 'text-review';
  const { chargeUpgradesImmediately } = useBillingMode();
  return (
    <div className="flex flex-col rounded-2xl border border-bordersoft bg-panel p-6">
      <h3 className={cn('text-lg font-semibold', accent)}>{title}</h3>
      <p className="mt-2 text-[13px] leading-relaxed text-muted">{blurb}</p>
      <div className="mt-4 flex flex-col gap-1.5">
        {visible.map((t) => {
          const perUnit = t.quota > 0 ? t.monthlyCents / 100 / t.quota : 0;
          return (
            <div
              key={t.quota}
              className="flex items-baseline justify-between gap-2 rounded-md border border-bordersoft bg-panel2 px-3 py-2 font-mono text-[12px]"
            >
              <span className="text-txt">
                {t.label}
                <span className="ml-2 text-faint">{t.quota.toLocaleString()} {unit}/mo</span>
              </span>
              <span className="flex items-baseline gap-2 tabular-nums">
                <span className="font-semibold text-txt">
                  €{(t.monthlyCents / 100).toFixed(2)}
                </span>
                <span className="text-faint">/mo</span>
                {perUnit > 0 && (
                  <span className="text-faint">· €{perUnit < 0.01 ? perUnit.toFixed(4) : perUnit.toFixed(2)}/{unit.slice(0, -1)}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-2">
        <Link to={`/features/${slug}`} className={cn('font-mono text-[11px] hover:underline', accent)}>
          Deep dive on {title} →
        </Link>
        <p className="text-[10.5px] text-faint">
          Stripe-billed · {chargeUpgradesImmediately ? 'immediate on upgrade' : 'pro-rated'} · move to Off any time
        </p>
      </div>
    </div>
  );
}

/**
 * Warmup is priced per mailbox by tier, not by a quota slider — so it gets its own card
 * sourced from the live tier catalog (/plans/warmup-tiers, public). The names, prices and
 * msg/day all come from the super-admin's per-tier config, so a BO edit shows here too.
 * The card is only mounted when feature('warmup_inbox') is on (which folds in "a tier is
 * actually priced"), so an empty/unpriced state never renders.
 */
function WarmupAddonCard({ slug, title, blurb }: { slug: string; title: string; blurb: string }) {
  const { data: tiers = [] } = useQuery({
    queryKey: ['plans', 'warmup-tiers-public'],
    queryFn: () => api<WarmupTierPublic[]>('/plans/warmup-tiers', { auth: false }),
    staleTime: 60_000,
  });
  const { chargeUpgradesImmediately } = useBillingMode();
  return (
    <div className="flex flex-col rounded-2xl border border-bordersoft bg-panel p-6">
      <h3 className="text-lg font-semibold text-allow">{title}</h3>
      <p className="mt-2 text-[13px] leading-relaxed text-muted">{blurb}</p>
      <div className="mt-4 flex flex-col gap-1.5">
        {tiers.map((t) => (
          <div
            key={t.wuiPlan}
            className="flex items-baseline justify-between gap-2 rounded-md border border-bordersoft bg-panel2 px-3 py-2 font-mono text-[12px]"
          >
            <span className="text-txt">
              {t.name}
              <span className="ml-2 text-faint">up to {t.maxMessagesPerDay.toLocaleString()}/day</span>
            </span>
            <span className="flex items-baseline gap-2 tabular-nums">
              <span className="font-semibold text-txt">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: t.currency.toUpperCase(),
                }).format(t.retailCents / 100)}
              </span>
              <span className="text-faint">/mo</span>
            </span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-2">
        <Link to={`/features/${slug}`} className="font-mono text-[11px] text-allow hover:underline">
          Deep dive on {title} →
        </Link>
        <p className="text-[10.5px] text-faint">
          per mailbox · Stripe-billed · {chargeUpgradesImmediately ? 'immediate on new mailbox' : 'pro-rated'}
        </p>
      </div>
    </div>
  );
}

// Default export required by React Router framework-mode route modules.
export default PricingPage;
